import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectHasSelectedCompanyFilterAccess } from 'containers/App/selectors'
import { MAP_INCIDENT_TYPE_TO_URL_PREFIX } from 'containers/ResolutionCenter/constants'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../../PartnerOptions/constants'

import {
  addCommentReplyError,
  addCommentReplySuccess,
  dismissInsightError,
  dismissInsightSuccess,
  getBlockedAttacksActivityLogError,
  getBlockedAttacksActivityLogSuccess,
  getCampaignsActivityLogError,
  getCampaignsActivityLogSuccess,
  getCommentsActivityLogError,
  getCommentsActivityLogSuccess,
  getIncidentsActivityLogError,
  getIncidentsActivityLogSuccess,
  getInsightsError,
  getInsightsSuccess,
} from './actions'
import {
  ADD_COMMENT_REPLY_REQUEST_ASYNC,
  BLOCKED_ATTACKS_ACTIVITY_LOG_REQUEST_ASYNC,
  CAMPAIGNS_ACTIVITY_LOG_REQUEST_ASYNC,
  COMMENTS_ACTIVITY_LOG_REQUEST_ASYNC,
  DISMISS_INSIGHTS_REQUEST_ASYNC,
  INCIDENTS_ACTIVITY_LOG_REQUEST_ASYNC,
  INSIGHTS_REQUEST_ASYNC,
} from './constants'

const incidentsActivityLogEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      INCIDENTS_ACTIVITY_LOG_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap(() =>
      !selectHasSelectedCompanyFilterAccess(state$.value)
        ? of(INCIDENTS_ACTIVITY_LOG_REQUEST_ASYNC.cancel())
        : api.dashboard
            .getIncidentsActivityLog(state$.value.partnerOptions.selectedCompany?.value)
            .pipe(
              map((response) => getIncidentsActivityLogSuccess(response)),
              catchError((error) => of(epicsErrorHandler(getIncidentsActivityLogError, error))),
              takeUntil(action$.pipe(ofType(INCIDENTS_ACTIVITY_LOG_REQUEST_ASYNC.CANCEL)))
            )
    )
  )

const campaignsActivityLogEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      CAMPAIGNS_ACTIVITY_LOG_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap(() =>
      !selectHasSelectedCompanyFilterAccess(state$.value)
        ? of(CAMPAIGNS_ACTIVITY_LOG_REQUEST_ASYNC.cancel())
        : api.dashboard
            .getCampaignsActivityLog(state$.value.partnerOptions.selectedCompany?.value)
            .pipe(
              map((response) => getCampaignsActivityLogSuccess(response)),
              catchError((error) => of(epicsErrorHandler(getCampaignsActivityLogError, error))),
              takeUntil(action$.pipe(ofType(CAMPAIGNS_ACTIVITY_LOG_REQUEST_ASYNC.CANCEL)))
            )
    )
  )

const commentsActivityLogEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      COMMENTS_ACTIVITY_LOG_REQUEST_ASYNC.INITIATE,
      ADD_COMMENT_REPLY_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap(() =>
      !selectHasSelectedCompanyFilterAccess(state$.value)
        ? of(COMMENTS_ACTIVITY_LOG_REQUEST_ASYNC.cancel())
        : api.dashboard
            .getCommentsActivityLog(state$.value.partnerOptions.selectedCompany?.value)
            .pipe(
              map((response) => getCommentsActivityLogSuccess(response)),
              catchError((error) => of(epicsErrorHandler(getCommentsActivityLogError, error))),
              takeUntil(action$.pipe(ofType(COMMENTS_ACTIVITY_LOG_REQUEST_ASYNC.CANCEL)))
            )
    )
  )

const blockedAttacksActivityLogEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      BLOCKED_ATTACKS_ACTIVITY_LOG_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap(() =>
      !selectHasSelectedCompanyFilterAccess(state$.value)
        ? of(BLOCKED_ATTACKS_ACTIVITY_LOG_REQUEST_ASYNC.cancel())
        : api.dashboard
            .getBlockedAttacksActivityLog(state$.value.partnerOptions.selectedCompany?.value)
            .pipe(
              map((response) => getBlockedAttacksActivityLogSuccess(response)),
              catchError((error) =>
                of(epicsErrorHandler(getBlockedAttacksActivityLogError, error))
              ),
              takeUntil(action$.pipe(ofType(BLOCKED_ATTACKS_ACTIVITY_LOG_REQUEST_ASYNC.CANCEL)))
            )
    )
  )

const addCommentReplyEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ADD_COMMENT_REPLY_REQUEST_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap((action) =>
      api.resolutionCenter
        .addReportComment(
          action.payload.reportId,
          {
            comment: action.payload.text,
            parentId: action.payload.commentId,
            isCommunity: false,
          },
          action.payload.reportTypePrefix
        )
        .pipe(
          map((response) => addCommentReplySuccess(response)),
          catchError((error) => of(epicsErrorHandler(addCommentReplyError, error))),
          takeUntil(action$.pipe(ofType(ADD_COMMENT_REPLY_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const insightsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      INSIGHTS_REQUEST_ASYNC.INITIATE,
      DISMISS_INSIGHTS_REQUEST_ASYNC.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap(() =>
      !selectHasSelectedCompanyFilterAccess(state$.value)
        ? of(INSIGHTS_REQUEST_ASYNC.cancel())
        : api.dashboard.getInsights(state$.value.partnerOptions.selectedCompany?.value).pipe(
            map((response) => getInsightsSuccess(response)),
            catchError((error) => of(epicsErrorHandler(getInsightsError, error))),
            takeUntil(action$.pipe(ofType(INSIGHTS_REQUEST_ASYNC.CANCEL)))
          )
    )
  )

const dismissInsightEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DISMISS_INSIGHTS_REQUEST_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    mergeMap((action) =>
      api.dashboard
        .dismissInsight(state$.value.partnerOptions.selectedCompany?.value, action.payload.data)
        .pipe(
          map((response) => dismissInsightSuccess(response)),
          catchError((error) => of(epicsErrorHandler(dismissInsightError, error))),
          takeUntil(action$.pipe(ofType(DISMISS_INSIGHTS_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  incidentsActivityLogEpic,
  campaignsActivityLogEpic,
  commentsActivityLogEpic,
  blockedAttacksActivityLogEpic,
  addCommentReplyEpic,
  insightsEpic,
  dismissInsightEpic
)
